import React, { createContext, useContext, useState, useCallback } from 'react';
import { isIOSNative, isAndroidNative } from '../utils/platform';
import NativePaywall from '../components/NativePaywall';
import WebPaywallSheet from '../components/WebPaywallSheet';
import { trackBoth, EVENTS } from '../lib/analytics';

/**
 * Global paywall controller.
 *
 *   const { openPaywall } = usePaywall();
 *   openPaywall('cry_analyzer');                    // → Stripe grid
 *   openPaywall('trackers_ribbon', { mode: 'trial' }); // → Trial CTA on top
 *
 * • iOS native      → RevenueCat sheet (Apple IAP)      — 3.1.1 compliant
 * • Android native  → RevenueCat sheet (Play Billing)   — Play §4.5 compliant
 * • Web / everything else → in-page bottom sheet with Stripe checkout
 */
const PaywallCtx = createContext({ openPaywall: () => {}, isOpen: false });

export const usePaywall = () => useContext(PaywallCtx);

export const PaywallProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [source, setSource] = useState('unknown');
  const [mode, setMode] = useState(null); // 'trial' | null

  const openPaywall = useCallback((src = 'unknown', opts = {}) => {
    trackBoth(EVENTS.PAYWALL_VIEWED, { source: src, mode: opts.mode || 'default' });
    setSource(src);
    setMode(opts.mode || null);
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    trackBoth(EVENTS.PAYWALL_DISMISSED, { source });
    setOpen(false);
  }, [source]);

  const onNative = isIOSNative() || isAndroidNative();

  return (
    <PaywallCtx.Provider value={{ openPaywall, isOpen: open }}>
      {children}
      {onNative ? (
        <NativePaywall open={open} onClose={handleClose} onSuccess={() => setOpen(false)} />
      ) : (
        <WebPaywallSheet open={open} onClose={handleClose} source={source} mode={mode} />
      )}
    </PaywallCtx.Provider>
  );
};

export default PaywallProvider;
